import { IconButton, TableCell, TableRow } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../../../../app/hooks";
import AddButton from "../../../../../common/components/functional/addButton/AddButton";
import IconWrapper from "../../../../../common/components/functional/icons/IconWrapper";
import { StripedTable } from "../../../../../common/components/functional/stripedTable/StripedTable";
import { StripedTableHeader } from "../../../../../common/components/functional/stripedTable/types";
import { localizeNames } from "../../../../../common/helpers/common";
import isEmpty from "../../../../../common/helpers/isEmpty";
import { ReactComponent as DeleteIcon } from "../../../../../common/icons/deleteDark.svg";
import { ReactComponent as EditIcon } from "../../../../../common/icons/editDark.svg";
import theme from "../../../../../theme";

type Props = {
  rules: any[];
  addRow?: () => void;
  editRow: (entry: any) => void;
  deleteRow: (id: string, version: number) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  readOnly?: boolean;
};

export const RulesTable = ({
  rules,
  addRow,
  editRow,
  deleteRow,
  isCollapsed,
  onToggleCollapse,
  readOnly = false,
}: Props) => {
  const { t } = useTranslation("");
  const { localized } = useAppSelector((state) => state);

  const HEADERS: StripedTableHeader[] = [
    { key: "customerCompany", label: t("customerCompany"), width: "25%" },
    {
      key: "customerOrganization",
      label: t("talentProfile:organization"),
      width: "25%",
    },
    { key: "workLocation", label: t("workLocation"), width: "20%" },
    { key: "workTitles", label: t("workTitles"), width: "20%" },
    { key: "actions", label: "", width: "10%" },
  ];

  const rows = rules.map((rule) => {
    const workTitleNames =
      rule.workTitles && localizeNames(localized.workTitle, rule.workTitles);
    return (
      <TableRow key={rule.id}>
        <TableCell width="15%">
          <Link
            target="_blank"
            to={`/home/customers/${rule.customerCompany?.id}/basicInfo?org=${rule.customerOrganization?.id}`}
            style={{
              textDecoration: "none",
              color: theme.palette.primary.main,
            }}
          >
            {rule.customerCompany?.name}
          </Link>
        </TableCell>
        <TableCell width="15%">{rule.customerOrganization?.name}</TableCell>
        <TableCell width="15%">
          {rule.workLocations?.map((loc: any) => loc.name).join(", ")}
        </TableCell>
        <TableCell width="15%">{workTitleNames}</TableCell>
        <TableCell
          width="2%"
          sx={{
            whiteSpace: "nowrap",
            minWidth: "90px",
            padding: 0,
            paddingRight: 1,
            textAlign: "right",
          }}
        >
          {!readOnly && (
            <>
              <IconButton
                onClick={() => editRow(rule)}
                sx={{ width: 44, height: 44 }}
              >
                <IconWrapper
                  icon={EditIcon}
                  sx={{
                    fill: "none",
                    stroke: "#000000de",
                    width: 18,
                    height: 18,
                    viewBox: "0 0 28 28",
                  }}
                />
              </IconButton>
              {!rule.isDefaultRule && (
                <IconButton onClick={() => deleteRow(rule.id, rule.version)}>
                  <DeleteIcon />
                </IconButton>
              )}
            </>
          )}
        </TableCell>
      </TableRow>
    );
  });

  const tableRows = !isEmpty(rows)
    ? rows
    : [
        <TableRow>
          <TableCell colSpan={5}>
            {t("talentProfile:targetedShifts.noRules")}
          </TableCell>
        </TableRow>,
      ];

  return (
    <>
      <StripedTable
        maxWidth="1800px"
        columnHeaders={HEADERS}
        itemRows={tableRows}
        isCollapsed={isCollapsed}
        onToggleCollapse={onToggleCollapse}
        headerSize="small"
      />
      {!readOnly && !isCollapsed && addRow && (
        <AddButton
          variant="outlined"
          onClick={addRow}
          label={t("talentProfile:targetedShifts.addRule")}
          sx={{ mt: 1 }}
        />
      )}
    </>
  );
};
